import styles from '../../assets/scss/components/collections/collections.module.scss'
import { AcfImage } from '../acf-image.client'
import { AcfImage as AcfImageType } from '../../lib/acf'
import { ProductCard } from './product-card.client'
import { classNames } from '@shopify/css-utilities'
import { Fragment, useCallback, useContext, useState } from 'react'
import { CollectionMetafields, ProductConnectionMetafields, ProductMetafields } from '../../lib/types'
import { BreakpointsContext } from '../layout/breakpoints-provider.client'
import { UseShopQueryResponse } from '@shopify/hydrogen'
import { useI18n } from '../i18n.client'
import { FuseResult } from 'fuse.js'

export const ProductGrid: React.FC<{
  isCollection?: boolean
  isSearch?: boolean
  pageBy: number
  pageIndex: number
  products: ProductConnectionMetafields | FuseResult<ProductMetafields>[]
  productsCount?: number
  quotes?: AcfImageType[][]
  url?: string
}> = ({
  isCollection = false,
  isSearch = false,
  pageBy,
  pageIndex: initialPageIndex,
  products: initialProducts,
  productsCount = 0,
  quotes = [],
  url,
}) => {
  const { isLargeUp, isMediumUp } = useContext(BreakpointsContext)
  const [pageIndex, setPageIndex] = useState(initialPageIndex)
  const parsedProducts = parseProducts({
    isLargeUp,
    isMediumUp,
    pageBy,
    pageIndex,
    products: initialProducts,
  })
  const [products, setProducts] = useState<ProductMetafields[]>(parsedProducts.products)
  const [cursor, setCursor] = useState(initialProducts.pageInfo.endCursor ?? '')
  const [hasNextPage, setHasNextPage] = useState(initialProducts.pageInfo.hasNextPage)
  const [isLoading, setIsLoading] = useState(false)
  const [quoteInjectionIndices, setQuoteInjectionIndices] = useState(parsedProducts.quoteInjectionIndices)

  let quoteIndex = 0

  const fetchProducts = useCallback(async () => {
    setIsLoading(true)

    const postUrl = new URL(window.location.origin + url)
    postUrl.searchParams.set('cursor', cursor)

    const response = await fetch(postUrl, { method: 'POST' })
    const { data }: UseShopQueryResponse<{ products: ProductConnectionMetafields }> = await response.json()
    const fetchedProducts = parseProducts({
      isLargeUp,
      isMediumUp,
      pageBy,
      pageIndex: pageIndex + 1,
      products: data.products,
    })

    setCursor(data.products.pageInfo.endCursor ?? '')
    setHasNextPage(data.products.pageInfo.hasNextPage)
    setIsLoading(false)
    setPageIndex(pageIndex + 1)
    setProducts([...products, ...fetchedProducts.products])
    setQuoteInjectionIndices([...quoteInjectionIndices, ...fetchedProducts.quoteInjectionIndices])

    window.history.replaceState({}, '', `${window.location.pathname}?page=${pageIndex + 1}`)
  }, [cursor, url])

  const fetchProductsFromCollection = useCallback(async () => {
    setIsLoading(true)

    const postUrl = new URL(window.location.origin + url)
    postUrl.searchParams.set('cursor', cursor)
    postUrl.searchParams.delete('sortKey', 'null')

    const response = await fetch(postUrl, { method: 'POST' })
    const { data }: UseShopQueryResponse<{ collection: CollectionMetafields }> = await response.json()
    const fetchedProducts = parseProducts({
      isLargeUp,
      isMediumUp,
      pageBy,
      pageIndex: pageIndex + 1,
      products: data.collection.products,
    })

    setCursor(data.collection.products.pageInfo.endCursor ?? '')
    setHasNextPage(data.collection.products.pageInfo.hasNextPage)
    setIsLoading(false)
    setPageIndex(pageIndex + 1)
    setProducts([...products, ...fetchedProducts.products])
    setQuoteInjectionIndices([...quoteInjectionIndices, ...fetchedProducts.quoteInjectionIndices])

    window.history.replaceState({}, '', `${window.location.pathname}?page=${pageIndex + 1}`)
  }, [cursor, url])

  return (
    <section className={classNames('section', 'px', styles.gridContainer)}>
      <div className={styles.grid}>
        {products.map((product, index) => (
          <Fragment key={product.id}>
            <ProductCard isOnGreen product={product} />
            {quoteInjectionIndices.includes(index) && quotes[quoteIndex] && (
              <AcfImage acf={quotes[quoteIndex++]} className="fit-width" />
            )}
          </Fragment>
        ))}
      </div>
      {!isSearch && (
        <>
          {/* <PaginationStatus pageBy={pageBy} pageIndex={pageIndex} productsCount={productsCount} /> */}
          <PaginationButton
            fetchProducts={isCollection ? fetchProductsFromCollection : fetchProducts}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
          />
        </>
      )}
    </section>
  )
}

const PaginationStatus: React.FC<{
  pageBy: number
  pageIndex: number
  productsCount: number
}> = ({ pageBy, pageIndex, productsCount }) => {
  const [i18n] = useI18n()

  if (!productsCount) {
    return null
  }

  let viewedCount = (pageIndex + 1) * pageBy
  if (viewedCount > productsCount) {
    viewedCount = productsCount
  }

  return (
    <div className={styles.paginationStatus}>
      {i18n.translate('pagination-status', { viewedCount, productsCount })}
      <div className={styles.paginationProgressContainer}>
        <div className={styles.paginationProgress} style={{ width: `${(viewedCount / productsCount) * 100}%` }}></div>
      </div>
    </div>
  )
}

const PaginationButton: React.FC<{
  fetchProducts: () => Promise<void>
  hasNextPage: boolean
  isLoading: boolean
}> = ({ fetchProducts, hasNextPage, isLoading }) => {
  const [i18n] = useI18n()

  if (!hasNextPage) {
    return null
  }

  return (
    <div className={styles.paginationButtonContainer}>
      <button className={styles.paginationButton} disabled={isLoading} onClick={fetchProducts}>
        {i18n.translate('load-more')}
      </button>
    </div>
  )
}

const parseProducts = ({
  isLargeUp = false,
  isMediumUp = false,
  pageBy,
  pageIndex,
  products,
}: {
  isLargeUp?: boolean
  isMediumUp?: boolean
  pageBy: number
  pageIndex: number
  products: ProductConnectionMetafields
}) => {
  const quoteInjectionIndices: number[] = []
  const offset = pageIndex * pageBy

  // Set up indices
  let heroInjectionIndices = 4
  if (isLargeUp) {
    heroInjectionIndices = 4
  } else if (isMediumUp) {
    heroInjectionIndices = 5
  }

  // Find the first hero...
  const heroIndex = products.nodes.findIndex((product) => product.heroImage)

  if (heroIndex >= 0) {
    // ...and move it
    const [heroProduct] = products.nodes.splice(heroIndex, 1)
    products.nodes.splice(heroInjectionIndices, 0, heroProduct)

    // Strip out any remaining hero images
    products.nodes.forEach((product, index) => {
      if (index !== heroInjectionIndices) {
        delete product.heroImage
      }
    })

    // Inject random quotes
    if (isLargeUp) {
      quoteInjectionIndices.push(4 + offset)
      quoteInjectionIndices.push(14 + offset)
    } else if (isMediumUp) {
      quoteInjectionIndices.push(3 + offset)
    } else {
      quoteInjectionIndices.push(4 + offset)
    }
  } else {
    quoteInjectionIndices.push(3 + offset)
  }

  return {
    products: products.nodes,
    quoteInjectionIndices,
  }
}
